import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useActionLock } from '@breaker/action-lock';
import { initBeacon, type BeaconHandle } from '@breaker/web-beacon';
import { Link, useNavigate } from '@tanstack/react-router';
import { authService } from '@/auth/hooks';
import { ApiError } from '@/auth/api';
import { tokenStore } from '@/auth/token-store';
import { Brand } from '@/components/brand';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { OtpInput } from '@/components/ui/otp-input';
import { Spinner } from '@/components/ui/spinner';
import { SetupMeter } from '@/components/setup-meter';
import { StepReveal } from '@/components/step-reveal';
import { RateCardEditor, seedTariffForm } from '@/components/rate-card-editor';
import { BrandEditor } from '@/components/brand-editor';
import { useFleets, useTariff, type TariffDefaults } from '@/data/fleets';
import { useFirstRun } from '@/data/first-run';
import { useDomainOrders } from '@/data/domain-orders';
import { fleetStore } from '@/lib/fleet-store';
import {
  buyExampleDomain,
  normaliseSlug,
  pickAvailableSlug,
  portalSlugHost,
} from '@/lib/slug-seed';
import { recordSetupEvent, wizardSkips } from '@/data/setup-wizard';
import { BookingPageLaunch } from '@/components/booking-page-launch';
import {
  DomainChoiceCards,
  WizardDomainBuyStep,
  WizardDomainByoStep,
  type DomainChoice,
} from '@/components/wizard-domain-step';
import { useQuery } from '@tanstack/react-query';

// domain-buy / domain-byo are the address step's sub-steps (position 3).
type Step = 'details' | 'code' | 'slug' | 'domain-buy' | 'domain-byo' | 'rates' | 'brand' | 'done';

/** Meter position — 'done' holds the bar full. */
const STEP_POSITION: Record<Step, number> = {
  details: 1,
  code: 2,
  slug: 3,
  'domain-buy': 3,
  'domain-byo': 3,
  rates: 4,
  brand: 5,
  done: 5,
};
const STEP_TOTAL = 5;

/** The door step 3 pre-selects on entry. Named rather than inlined so the
 *  funnel row and the initial state can never disagree about what "the
 *  default" was on the day a signup walked through. */
const DEFAULT_DOOR: DomainChoice = 'free';

/** Canonical comp code: uppercase, separators dropped. Mirrors the shared
 *  schema's transform so what the field shows is what the server compares. */
function canonicalCompCode(input: string): string {
  return input.trim().toUpperCase().replace(/[\s-]+/g, '');
}

const COMP_CODE_PATTERN = /^[2-9A-HJKMNP-TV-Z]{8,10}$/;

/**
 * P1 self-serve signup, resequenced into the setup wizard (setup-wizard
 * unit): details → emailed code → portal address (provisioning commits
 * here) → confirm rates → your brand → done. Steps 4–5 are the existing
 * Fleet Settings writes running authenticated against the fresh fleet;
 * both are skippable and land back on the in-app checklist unticked.
 * Mobile-first: 390px target, 16px inputs (.wizard-inputs), meter above
 * the fold.
 */
export function SignupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('details');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [code, setCode] = useState('');
  const [signupToken, setSignupToken] = useState('');
  // ---- 0084 comp code ----
  // Lives on the step that CREATES the fleet, so the tick box and the
  // request it changes are the same submit. Unchecked by default: nothing
  // is shown to the overwhelming majority of signups who have no code.
  const [hasCompCode, setHasCompCode] = useState(false);
  const [compCode, setCompCode] = useState('');
  const [compState, setCompState] = useState<'idle' | 'checking' | 'valid' | 'invalid'>(
    'idle',
  );
  // Set from the provisioning response: a code was sent but the last seat
  // went between the check and the submit. The done screen says so plainly.
  const [compMissed, setCompMissed] = useState(false);
  const [compApplied, setCompApplied] = useState(false);
  const [slug, setSlug] = useState('');
  const [slugState, setSlugState] = useState<
    'idle' | 'checking' | 'available' | 'reserved' | 'taken' | 'invalid'
  >('idle');
  const [fleetId, setFleetId] = useState<string | null>(null);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [domainChoice, setDomainChoice] = useState<DomainChoice>(DEFAULT_DOOR);
  // ---- Step-3 address default (address-default unit) ----
  // Once the user touches the slug field it is THEIRS: no reseed may
  // overwrite it, however the company name changes afterwards. A ref, not
  // state, because nothing renders off it — it only gates an effect — and
  // a re-render between the keystroke and the reseed would be a race.
  const slugEdited = useRef(false);
  // The value the seeder last pre-filled and already PROVED available.
  // The live availability effect skips exactly this value so Continue is
  // enabled on arrival instead of flickering through 'checking'.
  const seededSlug = useRef<string | null>(null);
  // Resume entry for the buy step: 'polling' when returning from Stripe.
  const [buyInitialSub, setBuyInitialSub] = useState<'search' | 'polling' | 'fallback'>('search');
  const [error, setError] = useState<string | null>(null);
  // Platform-level: is the buy-a-domain option offerable (keyed box)?
  const domainConfig = useQuery({
    queryKey: ['signup', 'config'],
    queryFn: () => authService.signupConfig(),
    staleTime: 5 * 60_000,
  });
  // Unit D: one lock across all step submits — Enter-key re-submits
  // while a request is in flight are dropped.
  const submitLock = useActionLock();
  const slugTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- Refresh resume (fleet + session already exist after step 3) ----
  // A signed-in visitor landing on /signup mid-setup re-enters at the
  // first step not yet completed-or-skipped; with nothing left, they go
  // straight in. Pre-OTP refreshes restart, same as before.
  const resuming = step === 'details' && !fleetId && tokenStore.isAuthenticated();
  // Public page: never fire the authed /me/fleets call while logged out —
  // only the resume path (already signed in) needs the fleet list.
  const fleetsQuery = useFleets(tokenStore.isAuthenticated());
  const resumeFleet =
    resuming && fleetsQuery.data
      ? (fleetsQuery.data.find(
          (f) => f.membership.role === 'owner' && f.fleet.trialEndsAt,
        ) ?? null)
      : null;
  const firstRun = useFirstRun(resumeFleet?.fleet.id ?? null, Boolean(resumeFleet));
  // For resume across the Stripe round-trip: the fleet's register order.
  const resumeOrders = useDomainOrders(resumeFleet?.fleet.id ?? null);
  useEffect(() => {
    if (!resuming) return;
    if (fleetsQuery.isLoading) return;
    // Authenticated (resuming implies it) but no trial fleet mid-setup —
    // a genuinely-finished / established user. The router no longer bounces
    // /signup, so send them on here.
    if (!resumeFleet) {
      void navigate({ to: '/' });
      return;
    }
    if (!firstRun.data) return;
    if (resumeOrders.isLoading) return;
    const id = resumeFleet.fleet.id;
    const done = new Map(firstRun.data.items.map((i) => [i.key, i.done]));
    setFleetId(id);
    fleetStore.set(id);
    setPortalUrl(
      resumeFleet.fleet.portalSlug
        ? `https://${resumeFleet.fleet.portalSlug}.portal.breakernetwork.app`
        : null,
    );
    // Domain round-trip first: a return marker (?domain=) or a live register
    // order routes into the buy step at the right sub-state, before the
    // rates/brand fallthrough. A succeeded/absent order falls through.
    const marker = new URLSearchParams(window.location.search).get('domain');
    const order = (resumeOrders.data ?? []).find((o) => o.kind === 'register') ?? null;
    const open = order && ['awaiting_payment', 'fulfilling', 'pending_operation'].includes(order.status);
    if (marker === 'processing' || order?.status === 'fulfilling' || order?.status === 'pending_operation') {
      setBuyInitialSub('polling');
      setStep('domain-buy');
      return;
    }
    if (marker === 'cancelled' || order?.status === 'failed' || order?.status === 'refunded' || open) {
      // Back-out, expiry, or an unfinished order: offer the free fallback
      // (which cancels any still-open order and frees the name).
      setBuyInitialSub('fallback');
      setStep('domain-buy');
      return;
    }
    if (!done.get('rates') && !wizardSkips.has(id, 'rates')) setStep('rates');
    else if (!done.get('brand') && !wizardSkips.has(id, 'brand')) setStep('brand');
    else void navigate({ to: '/' });
  }, [resuming, fleetsQuery.isLoading, resumeFleet, firstRun.data, resumeOrders.isLoading, resumeOrders.data, navigate]);

  // ---- Step funnel beacons (started/viewed; once-only server-side) ----
  useEffect(() => {
    if (!fleetId) return;
    if (step === 'rates') recordSetupEvent(fleetId, 'wizard_rates_started');
    if (step === 'brand') recordSetupEvent(fleetId, 'wizard_brand_started');
    if (step === 'done') recordSetupEvent(fleetId, 'wizard_done_viewed');
  }, [step, fleetId]);

  // ---- U1 web beacon (Phase A) — mounted ONLY here, torn down on unmount:
  // the wizard is the one Breaker-identity surface in this app (law 2).
  // Emits wizard_step on EVERY step transition; pre-fleet steps carry no id
  // (the server-derived signup events cover their is_test tagging).
  const webBeacon = useRef<BeaconHandle | null>(null);
  const beaconFleetId = useRef<string | null>(null);
  beaconFleetId.current = fleetId;
  useEffect(() => {
    const handle = initBeacon({ internalReferrerSuffix: 'breakernetwork.app' });
    webBeacon.current = handle;
    return () => {
      handle.teardown();
      webBeacon.current = null;
    };
  }, []);
  useEffect(() => {
    webBeacon.current?.event('wizard_step', {
      step,
      ...(beaconFleetId.current ? { fleetId: beaconFleetId.current } : {}),
    });
  }, [step]);

  // Server-side funnel beacon (funnel-emails unit). Separate from the web
  // beacon above: that one is session-scoped and bot-bucketed for the
  // traffic console, this one is the signup digest's own truth and carries
  // the email so a stalled signup can be named. The three address sub-steps
  // collapse to 'address' — they are one meter position and one decision.
  const emailRef = useRef('');
  emailRef.current = email.trim();
  useEffect(() => {
    const funnelStep =
      step === 'slug' || step === 'domain-buy' || step === 'domain-byo' ? 'address' : step;
    authService.signupStep({
      step: funnelStep,
      ...(emailRef.current ? { email: emailRef.current } : {}),
      ...(document.referrer ? { source: document.referrer } : {}),
    });
  }, [step]);

  /**
   * Pre-fill the free-subdomain door (address-default unit).
   *
   * THE one place a slug is ever seeded. Derived from (step, companyName)
   * rather than fired once on code-verify, which is what closes the
   * stale-seed finding: change the company name on step 2 and every
   * derived step-3 field — the slug, the portal host, the buy door's
   * worked example — reflows from it, because all three are pure
   * functions of this one value (see lib/slug-seed.ts).
   *
   * Two hard rules:
   *  - A hand-edited slug is NEVER overwritten. The field belongs to the
   *    user from their first keystroke onward.
   *  - A late-resolving check must not clobber a user who started typing
   *    while it was in flight — hence the re-check of slugEdited after the
   *    await, not just before it.
   */
  useEffect(() => {
    if (step !== 'slug') return;
    if (slugEdited.current) return;
    let cancelled = false;
    void pickAvailableSlug(companyName, (s) => authService.slugCheck(s)).then((seed) => {
      // The step may have moved on, or the user may have taken the field
      // over, during the availability walk.
      if (cancelled || slugEdited.current) return;
      setSlug(seed.slug);
      if (seed.available) {
        // Already proven — record it so the live check below skips this
        // exact value and Continue is enabled on arrival, not 400ms later.
        seededSlug.current = seed.slug;
        setSlugState('available');
      } else {
        seededSlug.current = null;
      }
    });
    return () => {
      cancelled = true;
    };
  }, [step, companyName]);

  // Live availability, debounced 300ms.
  useEffect(() => {
    if (step !== 'slug') return;
    if (slugTimer.current) clearTimeout(slugTimer.current);
    const value = normaliseSlug(slug);
    if (!value) {
      setSlugState('idle');
      return;
    }
    // Min-length + pattern only, deliberately NOT slug-seed's isSlugShaped:
    // that also enforces the 40-char cap, and firing this field's "3+
    // characters" message on an over-long slug would be a copy change to a
    // rule this unit isn't here to touch. The cap still governs the seeder,
    // where it decides how a variant suffix is made to fit.
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) || value.length < 3) {
      setSlugState('invalid');
      return;
    }
    // The seeder already proved this exact value available. Re-checking it
    // would flip to 'checking' and disable Continue on arrival, which is
    // the one thing this unit exists to prevent.
    if (seededSlug.current === value) return;
    setSlugState('checking');
    slugTimer.current = setTimeout(() => {
      authService
        .slugCheck(value)
        .then((r) =>
          setSlugState(r.available ? 'available' : (r.reason ?? 'taken')),
        )
        .catch(() => setSlugState('idle'));
    }, 300);
    return () => {
      if (slugTimer.current) clearTimeout(slugTimer.current);
    };
  }, [slug, step]);

  /**
   * On blur, not on keystroke: the endpoint is braked at 10/min and a
   * per-character check would burn that in one field. Advice only — an
   * invalid code never blocks Continue, because the server decides at
   * submit and an unusable code just means an ordinary trial.
   */
  function checkCompCode() {
    const value = canonicalCompCode(compCode);
    if (value === '') {
      setCompState('idle');
      return;
    }
    if (!COMP_CODE_PATTERN.test(value)) {
      // Same answer the server gives for a code that doesn't exist — the
      // field never reveals which kind of wrong it is.
      setCompState('invalid');
      return;
    }
    setCompState('checking');
    authService
      .compCodeCheck(value, email.trim() || undefined)
      .then((r) => setCompState(r.valid ? 'valid' : 'invalid'))
      // A failed check must not read as a bad code: fall silent and let the
      // submit be the authority.
      .catch(() => setCompState('idle'));
  }

  function onDetails(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submitLock.run(async () => {
      try {
        await authService.signupStart({
          name: name.trim(),
          email: email.trim(),
          companyName: companyName.trim(),
          ...(document.referrer ? { source: document.referrer } : {}),
        });
        setStep('code');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      }
    });
  }

  function onCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submitLock.run(async () => {
      try {
        const res = await authService.signupVerify(email.trim(), code.trim());
        if (res.status === 'signed_in') {
          // Existing account — no second trial; straight in.
          await navigate({ to: '/' });
          return;
        }
        setSignupToken(res.signupToken);
        // The slug is NOT seeded here any more. Seeding one-shot at this
        // point was the stale-seed finding: it could not reflow when the
        // company name changed on step 2, and it had no way to know
        // whether the user had already hand-edited the field. It is now a
        // single effect derived from (step, companyName) below.
        setStep('slug');
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "That code didn't work. Try again.");
      }
    });
  }

  function onSlug(e: FormEvent) {
    e.preventDefault();
    setError(null);
    submitLock.run(async () => {
      try {
        // Unticked means unticked: no code leaves the browser, whatever is
        // left in the (hidden, cleared) input.
        const canonicalComp = hasCompCode ? canonicalCompCode(compCode) : '';
        const res = await authService.signupComplete({
          signupToken,
          name: name.trim(),
          companyName: companyName.trim(),
          slug: normaliseSlug(slug),
          // Send it whenever it is shaped like a code — the server is the
          // authority, and a code the blur-check couldn't reach might still
          // be good.
          ...(COMP_CODE_PATTERN.test(canonicalComp) ? { compCode: canonicalComp } : {}),
          // Step-3 default telemetry: was the pre-selected door accepted,
          // and was the pre-filled slug kept? Counted only — the branch
          // below is what actually decides where the wizard goes.
          defaultDoor: DEFAULT_DOOR,
          chosenDoor: domainChoice,
          slugEdited: slugEdited.current,
        });
        setCompApplied(res.comp.applied);
        setCompMissed(res.comp.requested && !res.comp.applied);
        setFleetId(res.fleetId);
        setPortalUrl(res.portalUrl);
        fleetStore.set(res.fleetId);
        // Branch on the address choice. Buy is unreachable for is_test
        // fleets (the create call 403s them) — silently route to free so a
        // test signup is never dead-ended. The DEV override keeps ②
        // reachable for throwaway +wiz test signups.
        const canBuy =
          (domainConfig.data?.domainPurchase ?? false) &&
          (!res.isTest || (domainConfig.data?.testOverride ?? false));
        if (domainChoice === 'buy' && canBuy) {
          setBuyInitialSub('search');
          setStep('domain-buy');
        } else if (domainChoice === 'byo') {
          setStep('domain-byo');
        } else {
          setStep('rates');
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      }
    });
  }

  function skip(stepKey: 'rates' | 'brand') {
    if (fleetId) {
      recordSetupEvent(
        fleetId,
        stepKey === 'rates' ? 'wizard_rates_skipped' : 'wizard_brand_skipped',
      );
      wizardSkips.set(fleetId, stepKey);
    }
    setStep(stepKey === 'rates' ? 'brand' : 'done');
  }

  const wide = step === 'rates' || step === 'brand';

  return (
    <div className="min-h-screen flex flex-col wizard-inputs">
      <div className="h-1 bg-breaker-identity w-full" />

      {/* The one meter, visible without scrolling from first step to last. */}
      <div className="sticky top-0 z-10 bg-breaker-ground/95 backdrop-blur border-b border-breaker-line px-4 py-3">
        <div className={wide ? 'max-w-2xl mx-auto' : 'max-w-sm mx-auto'}>
          <SetupMeter
            label={
              step === 'done'
                ? 'Setting up your fleet — done'
                : `Setting up your fleet — step ${STEP_POSITION[step]} of ${STEP_TOTAL}`
            }
            current={STEP_POSITION[step]}
            total={STEP_TOTAL}
          />
        </div>
      </div>

      <main className="flex-1 px-4 py-8 sm:py-12">
        <div className={`${wide ? 'max-w-2xl' : 'max-w-sm'} mx-auto w-full`}>
          <div className="mb-8 text-center">
            <Brand size="lg" />
            <p className="text-sm text-breaker-mute mt-2 tracking-wide uppercase">
              {step === 'rates' || step === 'brand' || step === 'done'
                ? companyName || 'Your fleet'
                : 'Start your fleet'}
            </p>
          </div>

          <StepReveal key={step}>
          {resuming && (fleetsQuery.isLoading || (resumeFleet && !firstRun.data)) ? (
            <div className="py-10 flex justify-center">
              <Spinner />
            </div>
          ) : step === 'details' ? (
            <form onSubmit={onDetails} className="space-y-4">
              <Field label="Your name" htmlFor="suName">
                <Input
                  id="suName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  required
                  autoFocus
                />
              </Field>
              <Field label="Email" htmlFor="suEmail">
                <Input
                  id="suEmail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@yourfleet.co.uk"
                  required
                />
              </Field>
              <Field label="Company name" htmlFor="suCompany">
                <Input
                  id="suCompany"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  autoComplete="organization"
                  required
                />
              </Field>
              {error ? <ErrorBox message={error} /> : null}
              <Button type="submit" className="w-full" loading={submitLock.locked} size="lg">
                Continue
              </Button>
              <p className="text-center text-xs text-breaker-mute">
                30-day trial, everything on, no card needed.
              </p>
            </form>
          ) : step === 'code' ? (
            <form onSubmit={onCode} className="space-y-4">
              <p className="text-sm text-breaker-ink-2">
                We&rsquo;ve emailed a 6-digit code to{' '}
                <span className="font-medium text-breaker-ink">{email.trim()}</span>.
              </p>
              <div>
                <label className="block text-xs font-medium text-breaker-ink-2 mb-1.5">
                  Code
                </label>
                <OtpInput id="suCode" value={code} onChange={setCode} autoFocus />
              </div>
              {error ? <ErrorBox message={error} /> : null}
              <Button type="submit" className="w-full" loading={submitLock.locked} size="lg">
                Verify
              </Button>
              <button
                type="button"
                className="w-full text-center text-xs text-breaker-mute hover:text-breaker-identity transition-colors"
                onClick={() => {
                  setStep('details');
                  setCode('');
                }}
              >
                Wrong email? Go back
              </button>
            </form>
          ) : step === 'slug' ? (
            <form onSubmit={onSlug} className="space-y-5">
              {/* Address choice, split for clarity (required, no skip; same
                  three outcomes, same provisioning). TOP = the free address a
                  dispatch-and-app-only fleet needs; BOTTOM = the own-name
                  upgrade pair. Buy hidden on unkeyed boxes; is_test resolves to
                  free after provisioning. */}
              <section className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-breaker-ink">
                    Your free web address
                  </h3>
                  <p className="text-xs text-breaker-mute mt-0.5">
                    It powers your customers&rsquo; tracking links; the booking page can stay
                    off. Just want dispatch and the driver app? Pick this and carry on.
                  </p>
                </div>
                <Field label="Your customer portal address" htmlFor="suSlug">
                  <div className="flex items-center gap-1">
                    <Input
                      id="suSlug"
                      value={slug}
                      onChange={(e) => {
                        // First keystroke hands the field to the user for
                        // good — no later reseed may take it back.
                        slugEdited.current = true;
                        seededSlug.current = null;
                        setSlug(e.target.value);
                      }}
                      className="flex-1"
                      required
                      autoFocus
                    />
                  </div>
                </Field>
                <p className="text-xs text-breaker-mute -mt-1">
                  Customers will visit{' '}
                  <span className="font-mono text-breaker-ink-2">
                    https://{portalSlugHost(slug)}
                  </span>
                </p>
                <p className="text-xs">
                  {slugState === 'checking' ? (
                    <span className="text-breaker-mute">Checking…</span>
                  ) : slugState === 'available' ? (
                    <span className="text-breaker-green-deep">Available</span>
                  ) : slugState === 'taken' ? (
                    <span className="text-breaker-red">Already taken</span>
                  ) : slugState === 'reserved' ? (
                    <span className="text-breaker-red">That one&rsquo;s reserved</span>
                  ) : slugState === 'invalid' ? (
                    <span className="text-breaker-red">
                      Lowercase letters, numbers and hyphens, 3+ characters
                    </span>
                  ) : null}
                </p>
                <DomainChoiceCards
                  section="free"
                  choice={domainChoice}
                  onChoose={setDomainChoice}
                  buyEnabled={domainConfig.data?.domainPurchase ?? false}
                  price={
                    domainConfig.data?.tlds?.length
                      ? `£${Math.min(...domainConfig.data.tlds.map((t) => t.retailPence)) / 100}`
                      : null
                  }
                  exampleDomain={buyExampleDomain(companyName)}
                />
              </section>

              <section className="space-y-3 border-t border-breaker-line pt-4">
                <div>
                  <h3 className="text-sm font-semibold text-breaker-ink">
                    Want your own name on it?
                  </h3>
                  <p className="text-xs text-breaker-mute mt-0.5">
                    Put your own domain on the booking page — buy a fresh one or connect one
                    you already own. Everything else works exactly the same.
                  </p>
                </div>
                <DomainChoiceCards
                  section="upgrade"
                  choice={domainChoice}
                  onChoose={setDomainChoice}
                  buyEnabled={domainConfig.data?.domainPurchase ?? false}
                  price={
                    domainConfig.data?.tlds?.length
                      ? `£${Math.min(...domainConfig.data.tlds.map((t) => t.retailPence)) / 100}`
                      : null
                  }
                  exampleDomain={buyExampleDomain(companyName)}
                />
              </section>
              {/* 0084 — comp code. Unchecked shows nothing at all; this step
                  carries no copy about founding, pricing or free, because
                  the code's meaning is not this step's subject. */}
              <div className="border-t border-breaker-line pt-4 space-y-2">
                <label className="flex items-center gap-2 text-sm text-breaker-ink-2">
                  <input
                    type="checkbox"
                    checked={hasCompCode}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setHasCompCode(on);
                      // Unticking clears as well as hides — a stale value
                      // must not survive as invisible state.
                      if (!on) {
                        setCompCode('');
                        setCompState('idle');
                      }
                    }}
                    className="h-4 w-4 rounded border-breaker-line-2 text-breaker-identity focus:ring-breaker-identity"
                  />
                  I have a code
                </label>
                {hasCompCode ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Field label="Code" htmlFor="suComp">
                        <Input
                          id="suComp"
                          value={compCode}
                          onChange={(e) => {
                            setCompCode(e.target.value.toUpperCase());
                            setCompState('idle');
                          }}
                          onBlur={checkCompCode}
                          autoCapitalize="characters"
                          autoCorrect="off"
                          spellCheck={false}
                          autoComplete="off"
                          placeholder="e.g. AB12CD34"
                          // Typed text uppercases; the placeholder keeps its
                          // given casing (text-transform would shout it).
                          className="uppercase placeholder:normal-case"
                          autoFocus
                        />
                      </Field>
                    </div>
                    <p className="text-xs self-end pb-2 min-w-[7.5rem]">
                      {compState === 'checking' ? (
                        <span className="text-breaker-mute">Checking…</span>
                      ) : compState === 'valid' ? (
                        <span className="text-breaker-green-deep">Code applied</span>
                      ) : compState === 'invalid' ? (
                        // ONE message for every failure — unknown, expired,
                        // revoked, used up. Nothing here helps a prober.
                        <span className="text-breaker-red">That code isn&rsquo;t valid</span>
                      ) : null}
                    </p>
                  </div>
                ) : null}
              </div>
              {error ? <ErrorBox message={error} /> : null}
              <Button
                type="submit"
                className="w-full"
                loading={submitLock.locked}
                size="lg"
                disabled={slugState !== 'available'}
              >
                {domainChoice === 'free' ? 'Create my fleet' : 'Create my fleet & continue'}
              </Button>
            </form>
          ) : step === 'domain-buy' && fleetId ? (
            <div>
              <h2 className="text-lg font-semibold text-breaker-ink mb-1">Get your own domain</h2>
              <p className="text-sm text-breaker-mute mb-4">
                Search, register it in your name, and we set everything up. Your free subdomain is
                already live as a fallback.
              </p>
              <WizardDomainBuyStep
                fleetId={fleetId}
                seed={companyName || slug}
                slugHost={portalSlugHost(slug)}
                initialSub={buyInitialSub}
                onContinueFree={() => setStep('rates')}
                onDomainDone={() => setStep('rates')}
              />
            </div>
          ) : step === 'domain-byo' && fleetId ? (
            <div>
              <h2 className="text-lg font-semibold text-breaker-ink mb-1">Connect your domain</h2>
              <WizardDomainByoStep fleetId={fleetId} onDomainDone={() => setStep('rates')} />
            </div>
          ) : step === 'rates' && fleetId ? (
            <WizardRatesStep
              fleetId={fleetId}
              onDone={() => setStep('brand')}
              onSkip={() => skip('rates')}
            />
          ) : step === 'brand' && fleetId ? (
            <WizardBrandStep
              fleetId={fleetId}
              onDone={() => setStep('done')}
              onSkip={() => skip('brand')}
            />
          ) : step === 'done' ? (
            <div className="text-center space-y-5">
              <p className="text-2xl">🎉</p>
              <h2 className="text-xl font-semibold text-breaker-ink">
                Your booking page is live
              </h2>
              {/* 0084 — the two honest outcomes of a code, said once, here.
                  A missed code is NOT an error: the fleet exists and works,
                  it is simply on the standard trial. */}
              {compApplied ? (
                <p className="text-sm text-breaker-green-deep">
                  Founding fleet — no charge, everything switched on.
                </p>
              ) : compMissed ? (
                <p className="text-sm text-breaker-ink-2">
                  That code had already been used up, so you&rsquo;re on the standard
                  30-day trial instead — everything is switched on either way.
                </p>
              ) : null}
              {portalUrl ? (
                <p className="text-sm text-breaker-mute break-words">
                  Customers can book you right now at{' '}
                  <a
                    href={portalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ref text-breaker-identity hover:underline"
                  >
                    {portalUrl.replace('https://', '')}
                  </a>
                </p>
              ) : null}
              <div className="space-y-3">
                {portalUrl ? (
                  <BookingPageLaunch portalUrl={portalUrl} fleetId={fleetId} />
                ) : null}
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => void navigate({ to: '/' })}
                >
                  Enter your dashboard
                </Button>
              </div>
              {/* Point 4: free-subdomain choosers get the upgrade pointer. */}
              {domainChoice === 'free' && (domainConfig.data?.domainPurchase ?? false) ? (
                <p className="text-xs text-breaker-mute">
                  Want <span className="ref">{(companyName || slug).toLowerCase().replace(/[^a-z0-9]+/g, '')}.co.uk</span>{' '}
                  instead? Get a domain — £12/year, from Settings → Your portal address.
                </p>
              ) : (
                <p className="text-xs text-breaker-mute">
                  Anything you skipped is waiting on your setup checklist inside.
                </p>
              )}
            </div>
          ) : (
            <div className="py-10 flex justify-center">
              <Spinner />
            </div>
          )}
          </StepReveal>

          {step === 'details' || step === 'code' || step === 'slug' ? (
            <p className="mt-8 text-center text-xs text-breaker-mute">
              Already on Breaker?{' '}
              <Link to="/login" className="text-breaker-identity hover:underline">
                Sign in
              </Link>
            </p>
          ) : null}
        </div>
      </main>
      <footer className="px-6 py-6 text-center text-xs text-breaker-mute border-t border-breaker-line">
        © Breaker · Built for UK couriers
      </footer>
    </div>
  );
}

function StepIntro({
  title,
  body,
  onSkip,
}: {
  title: string;
  body: string;
  onSkip: () => void;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-breaker-ink">{title}</h2>
        <button
          type="button"
          onClick={onSkip}
          className="text-xs text-breaker-mute hover:text-breaker-identity transition-colors whitespace-nowrap"
        >
          Skip for now
        </button>
      </div>
      <p className="text-sm text-breaker-mute mt-1">{body}</p>
    </div>
  );
}

function WizardRatesStep({
  fleetId,
  onDone,
  onSkip,
}: {
  fleetId: string;
  onDone: () => void;
  onSkip: () => void;
}) {
  const tariffQuery = useTariff(fleetId);
  return (
    <div>
      <StepIntro
        title="Confirm your rates"
        body="The standard rate card is already loaded — check the numbers are yours and save. Your booking page prices with these."
        onSkip={onSkip}
      />
      {tariffQuery.isLoading || !tariffQuery.data ? (
        <div className="py-10 flex justify-center">
          <Spinner />
        </div>
      ) : (
        (() => {
          const { tariff, defaults } = tariffQuery.data;
          const card: TariffDefaults = tariff ?? defaults;
          return (
            <RateCardEditor
              key={`${fleetId}:${tariff ? tariff.updatedAt : 'defaults'}`}
              fleetId={fleetId}
              canEdit
              stored={Boolean(tariff)}
              initial={seedTariffForm(card)}
              submitLabel="Save & continue"
              onSaved={onDone}
            />
          );
        })()
      )}
    </div>
  );
}

function WizardBrandStep({
  fleetId,
  onDone,
  onSkip,
}: {
  fleetId: string;
  onDone: () => void;
  onSkip: () => void;
}) {
  const fleetsQuery = useFleets();
  const fleet = fleetsQuery.data?.find((f) => f.fleet.id === fleetId)?.fleet ?? null;
  return (
    <div>
      <StepIntro
        title="Your brand"
        body="Optional but worth it: your name is already on the page — add your colours or logo and the booking page wears them."
        onSkip={onSkip}
      />
      {!fleet ? (
        <div className="py-10 flex justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-breaker-ink">
            <span className="text-breaker-mute">Trading as</span>{' '}
            <span className="font-medium">{fleet.tradingName ?? fleet.legalName}</span>
          </p>
          <BrandEditor fleet={fleet} canEdit saveLabel="Save & continue" onSaved={onDone} />
        </div>
      )}
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="rounded-md bg-breaker-red/10 border border-breaker-red/30 px-3 py-2 text-sm text-breaker-red-deep">
      {message}
    </div>
  );
}
